import { Button } from "primereact/button";
import { DataView } from "primereact/dataview";
import { InputText } from "primereact/inputtext";
import { RadioButton } from "primereact/radiobutton";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { logService } from "src/core/services/logService";
import { textService } from "src/core/services/textService";
import { viewService } from "src/core/services/viewService";
import { useAppointmentStore } from "src/features/appointment/services/useAppointmentStore";
import { useAuthStore } from "src/features/auth/services/useAuthStore";
import { ClinicModel } from "src/features/clinics/models/clinicModel";
import { useClinicsStore } from "src/features/clinics/services/useClinicsStore";
import { useFavouriteStore } from "src/features/favourite/services/useFavouriteStore";
import { useParticipantStore } from "src/features/participant/services/useParticipantStore";
import { useSettingsStore } from "src/features/settings/services/useSettingsStore";
import type { AppointmentStepProps } from "../../AppointmentPage";
import "./AppointmentStep2.css";

export function AppointmentStep2({
  activeStepIndex,
  setActiveStepIndex,
}: AppointmentStepProps) {
  const searchNameRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const authStore = useAuthStore();
  const clinicsStore = useClinicsStore();
  const favouriteStore = useFavouriteStore();
  const participantStore = useParticipantStore();
  const appointmentStore = useAppointmentStore();
  const settingsStore = useSettingsStore();

  const clinicSelectOptions = [
    {
      key: "clinicSelectType1",
      label: t("Home.Appointments.ClinicSelectType1"),
      value: "favourite",
    },
    {
      key: "clinicSelectType2",
      label: t("Home.Appointments.ClinicSelectType2"),
      value: "all",
    },
  ];

  const [clinicSelectType, setClinicSelectType] = useState(
    clinicSelectOptions[favouriteStore.favourites.length > 0 ? 0 : 1]
  );
  const [clinics, setClinics] = useState<ClinicModel[]>([]);

  const [searchName, setSearchName] = useState("");
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  const searchNameFocus = () => {
    setTimeout(() => {
      searchNameRef.current?.focus();
    }, 100);
  };

  const onSearchNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setSearchName(value);
    }, 500);
  };

  useEffect(() => {
    appointmentStore.setSelectedClinic(undefined);
    appointmentStore.setSelectedDoctorProfile(undefined);
    appointmentStore.setSelectedDateTime(undefined);

    if (!viewService.isMobileSize) searchNameFocus();
  }, []);

  useEffect(() => {
    const clinics = clinicsStore.clinics
      .filter((clinic) => {
        return (
          searchName?.length === 0 ||
          clinic.name?.toLowerCase().includes(searchName?.toLowerCase() ?? "")
        );
      })
      .filter((clinic) => {
        return (
          clinicSelectType.value === "all" ||
          (clinicSelectType.value === "favourite" &&
            favouriteStore.favourites.some((fav) => fav.clinicId === clinic.id))
        );
      })
      .map((clinic) => {
        const favourite = favouriteStore.favourites.some(
          (fav) => fav.clinicId === clinic.id
        );

        return new ClinicModel({
          ...clinic,
          address: textService.formatAddress(clinic.address),
          favourite: favourite,
          order: (favourite ? "0" : "1") + clinic.name,
        });
      })
      .sort((a, b) => a.order.localeCompare(b.order, "hu"));

    if (logService.globalLog) logService.log("clinics", clinics);

    setClinics(clinics);
  }, [
    clinicsStore.clinics,
    favouriteStore.favourites,
    searchName,
    clinicSelectType,
  ]);

  const onClinicSelect = (clinic: ClinicModel) => {
    appointmentStore.setSelectedClinic(clinic);

    setActiveStepIndex(++activeStepIndex);
  };

  const hideSearch =
    settingsStore.clientConfiguration.subMode?.includes("hidesearch");

  return (
    <div className="appointmentStep2">
      <div className="col-12">
        <DataView
          value={clinics}
          header={
            <div className="flex flex-column sm:flex-row justify-content-between">
              <div className="flex flex-1">
                <Button
                  icon="pi pi-chevron-left"
                  label={t(
                    authStore.user.isLoggedIn
                      ? "Home.Appointments.Step1Title"
                      : "Home.Start"
                  )}
                  onClick={() => {
                    if (participantStore.participants?.length === 0) {
                      if (authStore.user.isLoggedIn) {
                        navigate("/home");
                      } else {
                        navigate("/");
                      }
                    } else {
                      setActiveStepIndex(--activeStepIndex);
                    }
                  }}
                ></Button>
              </div>
              <div className="flex flex-1 justify-content-center align-items-center gap-2">
                <div className="flex flex-column justify-content-center gap-1">
                  {hideSearch ? (
                    <h2 className="m-0 p-0">
                      {viewService.isMobileSize
                        ? ""
                        : t("Home.Appointments.Step2Title")}
                    </h2>
                  ) : (
                    <div className="flex align-items-center gap-1">
                      <div className="font-bold">{t("Base.Search")}</div>

                      <div className="p-inputgroup align-items-center searchInput">
                        <InputText
                          ref={searchNameRef}
                          id="searchName"
                          className="searchName w100"
                          defaultValue={searchName}
                          onChange={onSearchNameChange}
                          type="text"
                        />
                        <Button
                          icon="pi pi-times"
                          severity="warning"
                          onClick={() => {
                            if (searchNameRef.current)
                              searchNameRef.current.value = "";
                            setSearchName("");
                            searchNameFocus();
                          }}
                        ></Button>
                      </div>
                    </div>
                  )}
                  {favouriteStore.favourites.length > 0 && (
                    <div className="flex flex-column sm:flex-row gap-1">
                      {clinicSelectOptions.map((option) => {
                        return (
                          <div
                            key={option.key}
                            className="flex align-items-center"
                          >
                            <RadioButton
                              inputId={option.key}
                              name="category"
                              value={option}
                              onChange={(e) => setClinicSelectType(e.value)}
                              checked={clinicSelectType.key === option.key}
                            />
                            <label
                              htmlFor={option.key}
                              className="ml-2"
                            >
                              {option.label}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-1"></div>
            </div>
          }
          listTemplate={(items?: ClinicModel[]) => {
            if (!items || items.length === 0) return null;

            let list = items.map((item, index) => {
              return (
                <div
                  key={item.id}
                  className={
                    "col-12 sm:col-6 lg:col-4 xl:col-3 p-2 flex align-items-stretch " +
                    (index > 0 ? "border-top-1 surface-border" : "")
                  }
                >
                  <Button
                    text
                    raised
                    className="flex flex-column align-items-center gap-1 text-xl w100"
                    onClick={() => {
                      onClinicSelect(item);
                    }}
                  >
                    {!item.imagePath && (
                      <i
                        className="pi pi-plus-circle"
                        style={{ fontSize: "2rem" }}
                      ></i>
                    )}
                    {item.imagePath && (
                      <img
                        src={item.imagePath}
                        alt={item.name}
                        style={{ maxWidth: "150px" }}
                      />
                    )}
                    <div className="flex flex-row align-items-center gap-1">
                      {item.favourite && <i className="pi pi-star-fill"></i>}
                      <span className="font-bold">{item.name}</span>
                    </div>
                  </Button>
                </div>
              );
            });

            return (
              <div className="grid grid-nogutter justify-content-center">
                {list}
              </div>
            );
          }}
        />
      </div>
    </div>
  );
}
